<template>
  <div class="withdraws">
    <div class="filter-bar">
      <select v-model="filters.status" class="filter-select" @change="loadWithdraws">
        <option value="">全部状态</option>
        <option value="0">待审核</option>
        <option value="1">已通过</option>
        <option value="3">已打款</option>
        <option value="2">已驳回</option>
        <option value="4">失败</option>
      </select>
      <select v-model="filters.roleCode" class="filter-select" @change="loadWithdraws">
        <option value="">全部角色</option>
        <option value="runner">跑手</option>
        <option value="user">用户</option>
        <option value="community_agent">社区代理</option>
        <option value="shop_referrer">店家推广人</option>
        <option value="runner_referrer">跑手推广人</option>
      </select>
      <input
        v-model="filters.keyword"
        class="filter-input"
        placeholder="提现单号 / 账户ID"
        @keyup.enter="loadWithdraws"
      />
      <button class="btn btn-primary" @click="loadWithdraws">查询</button>
      <button class="btn btn-default" @click="resetFilters">重置</button>
    </div>

    <table class="data-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>提现单号</th>
          <th>账户角色</th>
          <th>用户</th>
          <th>手机/微信</th>
          <th>申请金额</th>
          <th>手续费</th>
          <th>实到金额</th>
          <th>提现渠道</th>
          <th>状态</th>
          <th>申请时间</th>
          <th>审核时间</th>
          <th>打款时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.withdraw_no }}</td>
          <td>{{ item.account_role_name || item.account_role_code }}</td>
          <td>
            <template v-if="item.user">
              {{ item.user.real_name || item.user.nickname || '未实名' }}
            </template>
            <span v-else class="muted">ID:{{ item.account_id }}</span>
          </td>
          <td>{{ item.user.phone || '' }}</td>
          <td class="amount minus">{{ (item.amount / 100).toFixed(2) }}</td>
          <td>{{ (item.service_fee / 100).toFixed(2) }}</td>
          <td class="amount plus">{{ (item.actual_amount / 100).toFixed(2) }}</td>
          <td>
            <span v-if="item.channel_info">{{ channelText(item.channel_info) }}</span>
            <span v-else class="muted">-</span>
          </td>
          <td>
            <span :class="['status-tag', statusText(item.status).cls]">{{ statusText(item.status).text }}</span>
          </td>
          <td>{{ item.created_at ? formatDateTime(item.created_at) : '-' }}</td>
          <td>{{ item.audit_time ? formatDateTime(item.audit_time) : '-' }}</td>
          <td>{{ item.pay_time ? formatDateTime(item.pay_time) : '-' }}</td>
          <td class="actions">
            <template v-if="item.status === 0">
              <button class="btn-xs btn-approve" @click="openDialog('approve', item)">通过</button>
              <button class="btn-xs btn-reject" @click="openDialog('reject', item)">驳回</button>
            </template>
            <template v-else-if="item.status === 1">
              <button class="btn-xs btn-pay" @click="openDialog('pay', item)">打款</button>
              <button class="btn-xs btn-reject" @click="openDialog('reject', item)">驳回</button>
              <button class="btn-xs btn-fail" @click="openDialog('fail', item)">标记失败</button>
            </template>
            <span v-else>无</span>
          </td>
        </tr>
        <tr v-if="list.length === 0">
          <td colspan="13" class="empty">暂无数据</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination" v-if="total > 0">
      <button class="page-btn" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页（共 {{ total }} 条）</span>
      <button class="page-btn" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)">下一页</button>
    </div>

    <!-- 二次确认弹窗（含备注输入框） -->
    <div class="modal-mask" v-if="dialog.visible" @click.self="closeDialog">
      <div class="modal">
        <div class="modal-header">{{ dialog.title }}</div>
        <div class="modal-body">
          <p class="modal-msg">{{ dialog.message }}</p>
          <template v-if="dialog.needRemark">
            <textarea
              v-model="dialog.remark"
              class="modal-textarea"
              :placeholder="dialog.placeholder"
              rows="3"
            ></textarea>
          </template>
          <template v-else-if="dialog.showRemark">
            <p class="modal-sub">备注（选填）：</p>
            <textarea
              v-model="dialog.remark"
              class="modal-textarea"
              placeholder="可填写备注"
              rows="2"
            ></textarea>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn btn-default" @click="closeDialog" :disabled="dialog.loading">取消</button>
          <button class="btn btn-primary" @click="confirmAudit" :disabled="dialog.loading">
            {{ dialog.loading ? '处理中…' : '确认' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 轻量操作反馈 -->
    <transition name="fade">
      <div v-if="toast.show" :class="['toast', toast.type]">{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import {
  getWithdraws,
  approveWithdraw,
  payWithdraw,
  rejectWithdraw,
  failWithdraw
} from '@/api/finance'
import { formatDateTime } from '@/utils/date'

export default {
  name: 'WithdrawAudit',
  data() {
    return {
      list: [],
      total: 0,
      currentPage: 1,
      filters: {
        status: '',
        roleCode: '',
        keyword: ''
      },
      dialog: {
        visible: false,
        action: '',
        item: null,
        title: '',
        message: '',
        remark: '',
        needRemark: false,
        showRemark: false,
        placeholder: '',
        required: false,
        loading: false
      },
      toast: { show: false, type: 'success', text: '' }
    }
  },
  mounted() {
    this.loadWithdraws()
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / 20) || 1
    }
  },
  methods: {
    async loadWithdraws(extra = {}) {
      try {
        const params = {
          page: this.currentPage,
          status: this.filters.status !== '' ? Number(this.filters.status) : undefined,
          account_role_code: this.filters.roleCode || undefined,
          keyword: this.filters.keyword || undefined,
          ...extra
        }
        const res = await getWithdraws(params)
        const paginator = res.data || {}
        this.list = paginator.data || []
        this.total = paginator.total || this.list.length
      } catch (e) {
        this.showToast('error', '加载失败')
      }
    },
    resetFilters() {
      this.filters = { status: '', roleCode: '', keyword: '' }
      this.currentPage = 1
      this.loadWithdraws()
    },
    changePage(page) {
      this.currentPage = page
      this.loadWithdraws()
    },
    statusText(status) {
      const map = {
        0: { text: '待审核', cls: 'pending' },
        1: { text: '已通过', cls: 'approved' },
        2: { text: '已驳回', cls: 'rejected' },
        3: { text: '已打款', cls: 'paid' },
        4: { text: '失败', cls: 'failed' }
      }
      return map[status] || { text: '未知', cls: '' }
    },
    channelText(info) {
      const parts = []
      if (info.channel_label) parts.push(info.channel_label)
      else if (info.channel) parts.push(info.channel)
      if (info.channel_account_name) parts.push(info.channel_account_name)
      if (info.channel_account) parts.push(info.channel_account)
      if (info.bank_name) parts.push(info.bank_name)
      return parts.length ? parts.join(' / ') : '已配置'
    },
    formatDateTime,
    openDialog(action, item) {
      const cfg = {
        approve: {
          title: '通过提现申请',
          message: `确认通过提现单 ${item.withdraw_no}（申请金额 ${(item.amount / 100).toFixed(2)}）？`,
          needRemark: false,
          showRemark: true,
          required: false,
          placeholder: ''
        },
        pay: {
          title: '确认打款',
          message: `确认已完成打款：提现单 ${item.withdraw_no}（实到 ${(item.actual_amount / 100).toFixed(2)}）？`,
          needRemark: false,
          showRemark: false,
          required: false,
          placeholder: ''
        },
        reject: {
          title: '驳回提现申请',
          message: '驳回后金额将退回申请人可用余额，请填写驳回原因。',
          needRemark: true,
          showRemark: false,
          required: true,
          placeholder: '请输入驳回原因'
        },
        fail: {
          title: '标记打款失败',
          message: '标记为打款失败后金额将退回申请人可用余额，请填写失败原因。',
          needRemark: true,
          showRemark: false,
          required: true,
          placeholder: '请输入失败原因'
        }
      }[action]

      this.dialog = {
        visible: true,
        action,
        item,
        remark: '',
        loading: false,
        ...cfg
      }
    },
    closeDialog() {
      if (this.dialog.loading) return
      this.dialog.visible = false
    },
    async confirmAudit() {
      const { action, item, remark, needRemark, required } = this.dialog
      if (needRemark && required && !remark.trim()) {
        this.showToast('error', '请填写原因')
        return
      }
      this.dialog.loading = true
      try {
        if (action === 'approve') await approveWithdraw(item.id, remark)
        else if (action === 'pay') await payWithdraw(item.id)
        else if (action === 'reject') await rejectWithdraw(item.id, remark)
        else if (action === 'fail') await failWithdraw(item.id, remark)
        this.dialog.visible = false
        this.showToast('success', '操作成功')
        this.loadWithdraws()
      } catch (e) {
        this.showToast('error', '操作失败，请重试')
      } finally {
        this.dialog.loading = false
      }
    },
    showToast(type, text) {
      this.toast = { show: true, type, text }
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => {
        this.toast.show = false
      }, 2500)
    }
  }
}
</script>

<style scoped>
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
}
.filter-input,
.filter-select {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.data-table th,
.data-table td {
  padding: 11px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}
.data-table th {
  background: #fafafa;
  font-weight: 600;
}
.data-table tr:hover td {
  background: #f9faff;
}
.empty {
  text-align: center;
  color: #999;
  padding: 30px 0;
}
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.btn-primary {
  background: #667eea;
  color: #fff;
}
.btn-default {
  background: #eee;
  color: #333;
}
.amount.plus {
  color: #2ecc71;
  font-weight: 600;
}
.amount.minus {
  color: #e74c3c;
  font-weight: 600;
}
.muted {
  color: #aaa;
}
.status-tag {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
}
.status-tag.pending {
  background: #eef4ff;
  color: #667eea;
}
.status-tag.approved {
  background: #e8f3ff;
  color: #409eff;
}
.status-tag.rejected {
  background: #fdecea;
  color: #e74c3c;
}
.status-tag.paid {
  background: #e8f8f0;
  color: #2ecc71;
}
.status-tag.failed {
  background: #fdf6ec;
  color: #e6a23c;
}
.actions {
  display: flex;
  gap: 6px;
}
.btn-xs {
  padding: 5px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #fff;
}
.btn-approve {
  background: #2ecc71;
}
.btn-pay {
  background: #409eff;
}
.btn-reject {
  background: #e74c3c;
}
.btn-fail {
  background: #e6a23c;
}
.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 16px;
}
.page-btn {
  padding: 6px 14px;
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}
.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.page-info {
  font-size: 14px;
  color: #666;
}
/* 二次确认弹窗 */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  width: 420px;
  max-width: 90vw;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.modal-header {
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
}
.modal-body {
  padding: 20px;
}
.modal-msg {
  margin: 0 0 12px;
  font-size: 14px;
  color: #333;
  line-height: 1.6;
}
.modal-sub {
  margin: 0 0 6px;
  font-size: 13px;
  color: #888;
}
.modal-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #f0f0f0;
}
/* 轻量 toast */
.toast {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 18px;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  z-index: 1100;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
}
.toast.success {
  background: #2ecc71;
}
.toast.error {
  background: #e74c3c;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
